import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { useCart } from "@/lib/cart";

export const REG_PRICES = { vipexp: 1000, selectp: 149, select: 129, general: 90 };

const ZONE_STYLES = {
  vipexp: {
    dot: "bg-[#F6D98A]/85",
    selected: "bg-[#F6D98A] shadow-[0_0_16px_rgba(246,217,138,1)]",
    label: "text-[#F6D98A]",
    pretty: "VIP Experience",
    size: "sz-front3",
  },
  selectp: {
    dot: "bg-[#E8C15A]/70",
    selected: "bg-[#E8C15A] shadow-[0_0_14px_rgba(232,193,90,0.95)]",
    label: "text-[#E8C15A]",
    pretty: "Select+ · Meet & Greet",
    size: "sz-vip",
  },
  select: {
    dot: "bg-[#7C74F2]/60",
    selected: "bg-[#8F88F7] shadow-[0_0_14px_rgba(143,136,247,0.95)]",
    label: "text-[#A5A0F7]",
    pretty: "Select",
    size: "sz-orch",
  },
  general: {
    dot: "bg-[#8FA5C9]/60",
    selected: "bg-[#A5B8DC] shadow-[0_0_14px_rgba(165,184,220,0.9)]",
    label: "text-[#9FB3D1]",
    pretty: "General",
    size: "sz-orch",
  },
  mezzanine: {
    dot: "bg-[#B07CF2]/50",
    selected: "bg-[#C08FF7] shadow-[0_0_14px_rgba(192,143,247,0.9)]",
    label: "text-[#C9A6F5]",
    pretty: "Mezzanine",
    size: "sz-mezz",
    soldOut: true,
  },
  balcony: {
    dot: "bg-[#6B7FA3]/50",
    selected: "bg-[#8FA5C9] shadow-[0_0_14px_rgba(143,165,201,0.9)]",
    label: "text-[#9FB3D1]",
    pretty: "Balcony",
    size: "sz-bal",
    soldOut: true,
  },
};

const SeatMap = ({ activeZone }) => {
  const [data, setData] = useState(null);
  const [hovered, setHovered] = useState(null);
  const { seats, toggleSeat, hasSeat } = useCart();

  useEffect(() => {
    apiGet("/api/seats")
      .then(setData)
      .catch(() => setData({ error: true }));
  }, []);

  if (!data) {
    return (
      <div className="h-[560px] flex items-center justify-center text-[#8A99B5] text-sm tracking-widest uppercase">
        Illuminating the house…
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="h-[300px] flex items-center justify-center text-[#C5CEE0]">
        The seat map could not be loaded — please refresh.
      </div>
    );
  }

  const full = seats.length >= 8;

  const clickSeat = (zone, rowLabel, seat) => {
    if (seat.status !== "available") return;
    const already = hasSeat(seat.id);
    if (!already && full) {
      toast.error("Up to 8 seats per order — remove one to add another.");
      return;
    }
    toggleSeat({
      id: seat.id,
      zone: zone.id,
      zoneLabel: ZONE_STYLES[zone.id].pretty,
      row: rowLabel,
      n: seat.n,
      price: seat.price,
      reg: REG_PRICES[zone.id] ?? seat.price,
    });
  };

  return (
    <div data-testid="seat-map-interactive-container" className="relative overflow-hidden">
      <div style={{ perspective: "1500px" }}>
        <div className="seatmap-tilt">
          {/* Stage */}
          <div className="relative mx-auto w-[64%] min-w-[280px]">
            <div className="absolute -inset-x-10 -top-6 bottom-0 rounded-[100%] bg-[#D4AF37]/20 blur-3xl glow-pulse" aria-hidden="true" />
            <div className="relative rounded-t-[14px] border-t-2 border-x border-[#D4AF37]/60 bg-gradient-to-b from-[#D4AF37]/25 to-[#D4AF37]/5 text-center py-4">
              <span className="font-cinzel tracking-[0.6em] text-[#F3D068] text-xs sm:text-sm">
                S T A G E
              </span>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mt-1">
                Susan Grau · A View from the Top — Live
              </p>
            </div>
          </div>

          {/* Zones — the whole house in one compact layout */}
          <div className="mt-8 space-y-5 sm:space-y-6 px-1">
            {data.zones.map((zone) => {
              const style = ZONE_STYLES[zone.id];
              const dimmed = activeZone && activeZone !== zone.id;
              const zoneSoldOut = zone.sold_out || style.soldOut;
              return (
                <div
                  key={zone.id}
                  className={`transition-opacity duration-500 ${dimmed ? "opacity-25" : "opacity-100"}`}
                >
                  <p className={`text-center text-[9px] sm:text-[10px] uppercase tracking-[0.35em] mb-2 ${zoneSoldOut ? "text-[#8A99B5]" : style.label}`}>
                    {style.pretty}
                    {zoneSoldOut && (
                      <span
                        data-testid={`zone-sold-out-${zone.id}`}
                        className="ml-3 text-[8px] sm:text-[9px] tracking-[0.22em] text-[#F3D068] border border-[#D4AF37]/40 rounded-full px-2.5 py-0.5 align-middle uppercase normal-case"
                      >
                        Sold Out
                      </span>
                    )}
                  </p>
                  <div className={zoneSoldOut ? "space-y-[1px]" : "space-y-[2px] sm:space-y-[4px]"}>
                    {zone.rows.map((r) => (
                      <div key={r.row} className="flex items-center justify-center gap-2.5 sm:gap-6">
                        {r.groups.map((group, gi) => (
                          <div key={gi} className="flex gap-[2px] sm:gap-[5px]">
                            {group.map((seat) => {
                              const selected = hasSeat(seat.id);
                              const sold = seat.status !== "available";
                              return (
                                <button
                                  key={seat.id}
                                  data-testid={`seat-${seat.id}`}
                                  disabled={sold}
                                  onMouseEnter={() => setHovered({ zone, row: r.row, seat })}
                                  onMouseLeave={() => setHovered(null)}
                                  onClick={() => clickSeat(zone, r.row, seat)}
                                  className={`seat-btn ${style.size} rounded-[2px] shrink-0 ${
                                    selected
                                      ? style.selected
                                      : sold
                                      ? "bg-white/10 opacity-25"
                                      : `${style.dot} hover:brightness-125`
                                  }`}
                                  style={{ opacity: selected || sold ? undefined : 0.7 }}
                                  aria-label={`${style.pretty} row ${r.row} seat ${seat.n}`}
                                />
                              );
                            })}
                          </div>
                        ))}
                        <span className="hidden md:block text-[9px] tracking-[0.2em] text-[#8A99B5]/60 w-6">
                          {r.row}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info + legend */}
      <div className="mt-7 flex flex-col items-center justify-between gap-4">
        <p className="text-sm text-[#C5CEE0] h-5 text-center" data-testid="seat-hover-info">
          {hovered
            ? `${ZONE_STYLES[hovered.zone.id].pretty} · Row ${hovered.row} · Seat ${hovered.seat.n} — $${
                hovered.seat.price
              }${hovered.seat.status === "available" ? "" : " · taken"}`
            : "Hover a seat to preview it · click to select"}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[#8A99B5]">
          {[
            ["#F6D98A", "Priority VIP"],
            ["#E8C15A", "VIP"],
            ["#7C74F2", "Orchestra"],
            ["#B07CF2", "Mezzanine · Sold Out"],
            ["#6B7FA3", "Balcony · Sold Out"],
          ].map(([c, l]) => (
            <span key={l} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: c, opacity: 0.7 }} /> {l}
            </span>
          ))}
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-white/10" /> Taken
          </span>
        </div>
      </div>
    </div>
  );
};

export default SeatMap;
